import React, { ReactElement, useState } from "react";

import {
  Asset,
  Presentation,
  RenderingObjectKind,
  UpdateRenderingObjectInput,
} from "../../../generated/gql/graphql";
import { LayoutFull } from "../../../hooks/use-layout";
import { Product } from "../../../hooks/use-products";
import {
  UpdateRenderingObjectProductInput,
  useUpdateLayoutById,
} from "../../../hooks/use-update-layout";
import { User } from "../../../hooks/use-user";
import { isNewRenderingObjectProductInput } from "../../../modals/adaption-selection-modal";
import { Size } from "../../../types/size";
import { Vector2 } from "../../../types/vector";
import {
  CanvasObjectText,
  DefaultText,
  toTextUpdateInput,
} from "../../texts/canvas-object-text";

import { AssetsArea } from "./assets-area";
import CanvasArea from "./canvas-area";
import {
  CanvasObject,
  CanvasObjectGraphic,
  CanvasObjectPresentation,
  getCanvasObjectsFromRenderingObjects,
} from "./canvas-object";
import CanvasPanel from "./canvas-panel";
import EditorContainer from "./editor-container";
import PanelArea from "./panel-area";
import PresentationSelectorModal, {
  PresentationSelectorModalProps,
} from "./presentation-selector-modal";
import { MaxCanvasPresentationObjects } from "./products-panel";

// Default Canvas Size
// NOTE: Do not change this, it is used in the backend as well for calculating rendering object offsets
const canvasSize: Size = {
  width: 600,
  height: 600,
};

export interface EditorProps {
  layout: LayoutFull;
  user: User | null | undefined;
}

export default function Editor({ layout, user }: EditorProps): ReactElement {
  const updateLayoutById = useUpdateLayoutById(layout.id);

  const [selectedCanvasObjectIndex, setSelectedCanvasObjectIndex] =
    useState<number>(-1);

  let [canvasObjects, setCanvasObjects] = useState<CanvasObject[]>(
    getCanvasObjectsFromRenderingObjects(layout.renderingObjects),
  );
  const [backgroundId, setBackgroundId] = useState<string | undefined>(
    layout.renderingBackground?.id,
  );
  const [scale, setScale] = useState<number | undefined>(
    layout.renderingBackground?.scale ?? 1,
  );
  const initialOffset = layout.renderingBackground?.offset;
  const [offset, setOffset] = useState<Vector2 | undefined>(
    initialOffset ? { x: initialOffset.x, y: initialOffset.y } : { x: 0, y: 0 },
  );
  const [presentationSelectorModalProps, setPresentationSelectorModalProps] =
    useState<PresentationSelectorModalProps | undefined>();
  const [nextCanvasObjectId, setNextCanvasOjectId] = useState<number>(1);

  // Set initial aspect ratio from layout
  const [aspectRatio, setAspectRatio] = useState<number | null>(
    layout.aspectRatio,
  );

  const [showTextAi, setShowTextAi] = useState(false);

  const handleBackgroundIdChange = async (
    newBackgroundId: string | undefined,
  ): Promise<void> => {
    setBackgroundId(newBackgroundId);
    updateLayoutById({
      renderingBackground: newBackgroundId
        ? {
            id: newBackgroundId,
            scale,
            offset,
          }
        : null,
    });
  };

  const handleBackgroundScaleChange = async (
    newScale: number | undefined,
  ): Promise<void> => {
    setScale(newScale);
  };

  const handleBackgroundScaleChangeEnd = async (
    newScale: number | undefined,
  ): Promise<void> => {
    setScale(newScale);
    updateLayoutById({
      renderingBackground: backgroundId
        ? {
            id: backgroundId,
            offset,
            scale: newScale,
          }
        : null,
    });
  };

  const handleBackgroundOffsetChange = async (
    newOffset: Vector2 | undefined,
  ): Promise<void> => {
    setOffset(newOffset);
    updateLayoutById({
      renderingBackground: backgroundId
        ? {
            id: backgroundId,
            scale,
            offset: newOffset,
          }
        : null,
    });
  };

  const handleBackgroundScaleOffsetChange = async (
    newScale: number | undefined,
    newOffset: Vector2 | undefined,
  ): Promise<void> => {
    setScale(newScale);
    setOffset(newOffset);
    updateLayoutById({
      renderingBackground: backgroundId
        ? {
            id: backgroundId,
            scale: newScale,
            offset: newOffset,
          }
        : null,
    });
  };

  const handleCanvasObjectsChanged = async (
    newCanvasObjects: CanvasObject[],
    saveRequired: boolean = true,
  ): Promise<void> => {
    setCanvasObjects(newCanvasObjects);

    // NOTE: Not nice, but currently required because multiple updates may occur in one frame
    canvasObjects = newCanvasObjects;

    if (saveRequired) {
      // Update on backend
      const renderingObjects: UpdateRenderingObjectInput[] =
        newCanvasObjects.map((obj) => {
          const updateInput: UpdateRenderingObjectInput = {
            id: obj.id,
            offset: obj.offset
              ? {
                  x: obj.offset.x,
                  y: obj.offset.y,
                }
              : undefined,
            scale: obj.scale,
          };
          switch (obj.kind) {
            case RenderingObjectKind.Presentation:
              return {
                ...updateInput,
                presentationId: obj.presentationId,
                productInputs: obj.productInputs?.map((productInput) => {
                  if (isNewRenderingObjectProductInput(productInput)) {
                    return {
                      identifier: productInput.identifier,
                      file: productInput.file,
                    };
                  } else {
                    return {
                      identifier: productInput.identifier,
                    };
                  }
                }),
              };
            case RenderingObjectKind.Graphic:
              return {
                ...updateInput,
                graphicId: obj.graphicId,
              };
            case RenderingObjectKind.Text:
              return {
                ...updateInput,
                text: toTextUpdateInput(obj.text),
              };
          }
        });
      updateLayoutById({
        renderingObjects,
      });
    }
  };

  const handleCanvasObjectPresentationChanged = async (
    index: number,
    changes: Partial<Omit<CanvasObjectPresentation, "kind">>,
  ): Promise<void> => {
    // Change canvas object
    const newCanvasObject = { ...canvasObjects[index], ...changes };
    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects[index] = newCanvasObject;
    const saveRequired =
      !!changes.offset ||
      !!changes.scale ||
      !!changes.presentationId ||
      !!changes.productInputs;
    await handleCanvasObjectsChanged(newCanvasObjects, saveRequired);
  };

  const handleCanvasObjectGraphicChanged = async (
    index: number,
    changes: Partial<Omit<CanvasObjectGraphic, "kind">>,
  ): Promise<void> => {
    // Change canvas object
    const newCanvasObject = { ...canvasObjects[index], ...changes };
    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects[index] = newCanvasObject;
    const saveRequired = !!changes.offset || !!changes.scale;
    await handleCanvasObjectsChanged(newCanvasObjects, saveRequired);
  };

  const handleCanvasObjectTextChanged = async (
    index: number,
    changes: Partial<Omit<CanvasObjectText, "kind">>,
  ): Promise<void> => {
    // Change canvas object
    const newCanvasObject = { ...canvasObjects[index], ...changes };
    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects[index] = newCanvasObject;
    const saveRequired = !!changes.offset || !!changes.scale || !!changes.text;
    await handleCanvasObjectsChanged(newCanvasObjects, saveRequired);
  };

  function handleCanvasObjectChanged(
    index: number,
    kind: RenderingObjectKind,
    changes:
      | Partial<CanvasObjectPresentation>
      | Partial<CanvasObjectGraphic>
      | Partial<CanvasObjectText>,
  ): Promise<void> {
    switch (kind) {
      case RenderingObjectKind.Presentation:
        return handleCanvasObjectPresentationChanged(
          index,
          changes as Partial<Omit<CanvasObjectPresentation, "kind">>,
        );
      case RenderingObjectKind.Graphic:
        return handleCanvasObjectGraphicChanged(
          index,
          changes as Partial<Omit<CanvasObjectGraphic, "kind">>,
        );
      case RenderingObjectKind.Text:
        return handleCanvasObjectTextChanged(
          index,
          changes as Partial<Omit<CanvasObjectText, "kind">>,
        );
    }
  }

  async function handleCanvasObjectAdded(
    newCanvasObject: CanvasObject,
  ): Promise<void> {
    setNextCanvasOjectId(nextCanvasObjectId + 1);
    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects.push(newCanvasObject);
    await handleCanvasObjectsChanged(newCanvasObjects);
    setSelectedCanvasObjectIndex(newCanvasObjects.length - 1);
  }

  const handleCanvasObjectDuplicate = async (index: number): Promise<void> => {
    const originalCanvasObject = canvasObjects[index];
    const originalOffset = originalCanvasObject.offset;

    if (
      originalCanvasObject.kind === RenderingObjectKind.Presentation &&
      canvasObjects.length >= MaxCanvasPresentationObjects
    ) {
      alert(
        "Maximum number of products reached. " +
          `You have already added ${canvasObjects.length}/${MaxCanvasPresentationObjects} products. ` +
          "Please delete a product from the scene to add a new one.",
      );

      return;
    }

    // Add canvas object
    let newCanvasObject: CanvasObject;
    switch (originalCanvasObject.kind) {
      case RenderingObjectKind.Presentation:
        newCanvasObject = {
          key: `new-${nextCanvasObjectId}`,
          kind: originalCanvasObject.kind,
          lockPosition: false,
          lockScale: false,
          presentation: originalCanvasObject.presentation,
          presentationId: originalCanvasObject.presentationId,
          offset: {
            x: originalOffset ? originalOffset.x + 50 : 50,
            y: originalOffset ? originalOffset.y + 50 : 50,
          },
          scale: originalCanvasObject.scale,
          productInputs: null,
        };
        break;
      case RenderingObjectKind.Graphic:
        newCanvasObject = {
          key: `new-${nextCanvasObjectId}`,
          kind: originalCanvasObject.kind,
          lockPosition: false,
          lockScale: false,
          graphicId: originalCanvasObject.graphicId,
          offset: {
            x: originalOffset ? originalOffset.x + 50 : 50,
            y: originalOffset ? originalOffset.y + 50 : 50,
          },
          scale: originalCanvasObject.scale,
        };
        break;
      case RenderingObjectKind.Text:
        newCanvasObject = {
          key: `new-${nextCanvasObjectId}`,
          kind: originalCanvasObject.kind,
          lockPosition: false,
          lockScale: false,
          text: {
            fontId: originalCanvasObject.text.fontId,
            content: originalCanvasObject.text.content,
            color: originalCanvasObject.text.color,
            width: originalCanvasObject.text.width,
            height: originalCanvasObject.text.height,
            alignHorizontal: originalCanvasObject.text.alignHorizontal,
            alignVertical: originalCanvasObject.text.alignVertical,
          },
          offset: {
            x: originalOffset ? originalOffset.x + 50 : 50,
            y: originalOffset ? originalOffset.y + 50 : 50,
          },
          scale: originalCanvasObject.scale,
        };
        break;
      default:
        return;
    }
    await handleCanvasObjectAdded(newCanvasObject);
  };

  const handleCanvasPresentationObjectAdded = (
    presentationId: string,
    presentation: Pick<
      Presentation,
      | "referenceResolution"
      | "id"
      | "name"
      | "productId"
      | "type"
      | "thumbnailUrl"
      | "url"
    > & {
      asset: Pick<Asset, "width" | "height"> | null;
    },
  ): void => {
    // Calculate initial offset to center the presentation on the canvas
    let canvasObjectOffset = null;
    if (presentation.asset) {
      const referenceResolutionScale =
        canvasSize.width / presentation.referenceResolution;
      const canvasObjectSize = {
        width: presentation.asset.width * referenceResolutionScale,
        height: presentation.asset.height * referenceResolutionScale,
      };
      let offsetX = (canvasSize.width - canvasObjectSize.width) / 2;
      let offsetY = (canvasSize.height - canvasObjectSize.height) / 2;
      canvasObjectOffset = { x: offsetX, y: offsetY };
    }

    // Add canvas object
    const newCanvasObject: CanvasObject = {
      key: `new-${nextCanvasObjectId}`,
      kind: RenderingObjectKind.Presentation,
      lockPosition: false,
      lockScale: false,
      presentationId,
      presentation,
      offset: canvasObjectOffset,
      scale: 1,
      productInputs: null,
    };
    handleCanvasObjectAdded(newCanvasObject);
  };

  const handleCanvasObjectDeleted = (index: number): void => {
    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects.splice(index, 1);
    handleCanvasObjectsChanged(newCanvasObjects);
    if (index === selectedCanvasObjectIndex) {
      setSelectedCanvasObjectIndex(-1);
    }
  };

  const handleCanvasObjectMoved = (index: number, newIndex: number): void => {
    const canvasObject = canvasObjects.at(index);
    if (!canvasObject) {
      return;
    }

    if (newIndex < 0 || newIndex >= canvasObjects.length) {
      return;
    }

    const newCanvasObjects = [...canvasObjects];
    newCanvasObjects.splice(index, 1);
    newCanvasObjects.splice(newIndex, 0, canvasObject);
    handleCanvasObjectsChanged(newCanvasObjects);
    if (index === selectedCanvasObjectIndex) {
      setSelectedCanvasObjectIndex(newIndex);
    }
  };

  const handleCanvasObjectSelected = (index: number): void => {
    setSelectedCanvasObjectIndex(index);
  };

  const handleProductInputsChange = async (
    index: number,
    productInputs: UpdateRenderingObjectProductInput[],
  ): Promise<void> => {
    await handleCanvasObjectPresentationChanged(index, { productInputs });
  };

  const handleProductSelected = (product: Product): void => {
    const { perspectives, animations, id } = product;
    const hasPerspectives = perspectives && perspectives.length > 0;
    const hasAnimations = animations && animations.length > 0;

    if (perspectives && perspectives.length === 1 && !hasAnimations) {
      // Product has only one perspective
      const presentation = perspectives[0];
      handleCanvasPresentationObjectAdded(presentation.id, presentation);
    } else if (animations && animations.length === 1 && !hasPerspectives) {
      // Product has only one animation
      const presentation = animations[0];
      handleCanvasPresentationObjectAdded(presentation.id, presentation);
    } else {
      // Show modal for selection
      setPresentationSelectorModalProps({
        productId: id,
        selectedPresentationId: undefined,
        onClose: () => setPresentationSelectorModalProps(undefined),
        onSelect: (presentation) => {
          handleCanvasPresentationObjectAdded(presentation.id, presentation);
          setPresentationSelectorModalProps(undefined);
        },
      });
    }
  };

  const selectedCanvasObject =
    selectedCanvasObjectIndex >= 0
      ? canvasObjects.at(selectedCanvasObjectIndex)
      : undefined;

  const selectedProductId =
    selectedCanvasObject?.kind === RenderingObjectKind.Presentation
      ? (selectedCanvasObject.presentation?.productId ?? undefined)
      : undefined;

  async function handleAspectRatioChange(
    newAspectRatio: number | null,
  ): Promise<void> {
    setAspectRatio(newAspectRatio);

    // Save aspect ratio to layout
    updateLayoutById({
      aspectRatio: newAspectRatio,
    });
  }

  function handleGraphicAdded(
    graphicId: string,
    width: number,
    height: number,
  ): void {
    // Calculate initial scale and position for the new graphic object
    let graphicScale = 1;
    if (width > canvasSize.width || height > canvasSize.height) {
      const scaleX = canvasSize.width / width;
      const scaleY = canvasSize.height / height;
      graphicScale = Math.min(scaleX, scaleY) * 0.8; // Scale down to 80% to ensure it fits well within the canvas
    }

    // Calculate initial offset to center the graphic on the canvas
    const offsetX = (canvasSize.width - width * graphicScale) / 2;
    const offsetY = (canvasSize.height - height * graphicScale) / 2;

    // Add graphic to layout
    handleCanvasObjectAdded({
      key: `graphic-${graphicId}`,
      kind: RenderingObjectKind.Graphic,
      lockPosition: false,
      lockScale: false,
      graphicId,
      scale: graphicScale,
      offset: { x: offsetX, y: offsetY },
    });
  }

  function handleAddText(): void {
    // Add text to layout
    handleCanvasObjectAdded({
      key: `text-${nextCanvasObjectId}`,
      kind: RenderingObjectKind.Text,
      lockPosition: false,
      lockScale: false,
      text: {
        ...DefaultText,
      },
      scale: 1,
      offset: { x: canvasSize.width / 2, y: canvasSize.height / 2 },
    });
  }

  return (
    <EditorContainer>
      <CanvasArea>
        <CanvasPanel
          layout={layout}
          backgroundId={backgroundId}
          scale={scale}
          scaleChange={handleBackgroundScaleChange}
          scaleChangeEnd={handleBackgroundScaleChangeEnd}
          offset={offset}
          offsetChange={handleBackgroundOffsetChange}
          scaleOffsetChange={handleBackgroundScaleOffsetChange}
          selectedCanvasObject={selectedCanvasObject}
          selectedCanvasObjectChange={(changes) =>
            handleCanvasObjectChanged(
              selectedCanvasObjectIndex,
              selectedCanvasObject
                ? selectedCanvasObject.kind
                : RenderingObjectKind.Presentation,
              changes,
            )
          }
          selectedCanvasObjectIndex={selectedCanvasObjectIndex}
          canvasObjects={canvasObjects}
          objectChanged={handleCanvasObjectChanged}
          objectDuplicated={handleCanvasObjectDuplicate}
          objectDeleted={handleCanvasObjectDeleted}
          objectMoved={handleCanvasObjectMoved}
          objectSelected={handleCanvasObjectSelected}
          handleProductInputsChange={handleProductInputsChange}
          aspectRatio={aspectRatio}
          aspectRatioChange={handleAspectRatioChange}
          finalAspectRatio={
            aspectRatio ??
            layout.project?.workspace?.defaultAspectRatio ??
            user?.activeClient?.defaultAspectRatio ??
            1
          }
          canvasSize={canvasSize}
          showTextAi={showTextAi}
          handleShowTextAi={setShowTextAi}
        />
      </CanvasArea>
      <PanelArea>
        <AssetsArea
          backgroundId={backgroundId}
          setBackgroundId={handleBackgroundIdChange}
          onGraphicAdded={handleGraphicAdded}
          onAddText={handleAddText}
          showTextAi={showTextAi}
          onShowTextAi={setShowTextAi}
          selectedProductId={selectedProductId}
          canvasObjects={canvasObjects}
          productSelected={handleProductSelected}
          objectChanged={handleCanvasObjectChanged}
          selectedCanvasObjectIndex={selectedCanvasObjectIndex}
          selectedCanvasObject={selectedCanvasObject}
          selectedObjectChanged={(changes) => {
            if (!selectedCanvasObject) {
              return;
            }
            handleCanvasObjectChanged(
              selectedCanvasObjectIndex,
              selectedCanvasObject.kind,
              changes,
            );
          }}
          setShowTextAi={setShowTextAi}
        />
      </PanelArea>
      {presentationSelectorModalProps && (
        <PresentationSelectorModal {...presentationSelectorModalProps} />
      )}
    </EditorContainer>
  );
}
